import React, { useEffect, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Chip,
    Stack,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
    getQuotas,
    IQuotaItem,
    updateQuotaStatus,
} from 'services/quota.service';
import { handleError } from 'utils/helpers';
import { IQuestionPage } from 'services/campaignPageLogic.service';

interface QuotaTableProps {
    surveyId: number;
    questionPageId?: number;
    refreshTrigger?: number;
    questionPages?: IQuestionPage[];
}

const QuotaTable: React.FC<QuotaTableProps> = ({
    surveyId,
    questionPageId,
    refreshTrigger = 0,
    questionPages,
}) => {
    const [quotas, setQuotas] = useState<IQuotaItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(
        null
    );

    // Fetch quotas from API
    const fetchQuotas = async () => {
        if (!surveyId) return;

        setLoading(true);
        try {
            const response = await getQuotas(surveyId, questionPageId);
            if (response.data?.quotas) {
                setQuotas(response.data.quotas);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch quotas on mount or when parameters change
    useEffect(() => {
        fetchQuotas();
    }, [surveyId, questionPageId, refreshTrigger]);

    // Handle toggle quota status
    const handleToggleStatus = async (quota: IQuotaItem) => {
        const quotaId = Number(quota.id);
        const newStatus =
            quota.attributes.status === 'active' ? 'inactive' : 'active';

        setUpdatingStatusId(String(quota.id));
        try {
            await updateQuotaStatus(quotaId, {
                quota: { status: newStatus as 'active' | 'inactive' },
            });

            toast.success(
                `Cập nhật trạng thái thành ${
                    newStatus === 'active' ? 'Hoạt động' : 'Không hoạt động'
                } thành công`
            );

            // Update quota in list
            setQuotas((prev) =>
                prev.map((q) =>
                    Number(q.id) === quotaId
                        ? {
                              ...q,
                              attributes: {
                                  ...q.attributes,
                                  status: newStatus as 'active' | 'inactive',
                              },
                          }
                        : q
                )
            );
        } catch (error) {
            handleError(error);
        } finally {
            setUpdatingStatusId(null);
        }
    };

    // Helper: Get question by ID from questionPages
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getQuestionById = (
        questionPage: IQuestionPage,
        questionId: number
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): any | null => {
        return questionPage.attributes.questions.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (q: any) => Number(q.id) === questionId
        );
    };

    // Helper: Get answer option by ID from question
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getAnswerOptionById = (
        question: any, // eslint-disable-line @typescript-eslint/no-explicit-any
        optionId: number
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): any | null => {
        if (question?.attributes?.answer_options) {
            return question.attributes.answer_options.find(
                (ans: any) => Number(ans.id) === optionId // eslint-disable-line @typescript-eslint/no-explicit-any
            );
        }
        return null;
    };

    // Helper: Truncate text to max length
    const truncateText = (text: string, maxLength = 50): string => {
        if (!text) return '';
        return text.length > maxLength
            ? `${text.substring(0, maxLength)}...`
            : text;
    };

    // Helper: Render conditions for a quota
    const renderConditions = (quota: IQuotaItem) => {
        const conditions = quota.attributes.quota_conditions || [];
        const questionPageId = quota.attributes.question_page_id;
        if (conditions.length === 0) {
            return <Typography variant="body2">Không có điều kiện</Typography>;
        }
        const questionPage = questionPages?.find(
            (page) => page.id === String(questionPageId)
        );
        if (!questionPage) {
            return (
                <Typography variant="body2">
                    Không tìm thấy trang câu hỏi
                </Typography>
            );
        }

        return (
            <Stack spacing={0.5}>
                {conditions.map((cond, idx) => {
                    const question = getQuestionById(
                        questionPage,
                        cond.question_id
                    );
                    const answer = question
                        ? getAnswerOptionById(question, cond.answer_option_id)
                        : null;

                    const questionTitle =
                        question?.attributes?.title || 'Câu hỏi không xác định';
                    const answerText =
                        answer?.text || 'Tùy chọn không xác định';

                    return (
                        <Box key={idx} sx={{ fontSize: '0.85rem' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                }}
                            >
                                <strong>{truncateText(questionTitle)}</strong>
                                <br />
                                <span style={{ color: '#666' }}>
                                    {truncateText(answerText)}
                                </span>
                            </Typography>
                        </Box>
                    );
                })}
            </Stack>
        );
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={300}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (quotas.length === 0) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
            >
                <Typography color="text.secondary">
                    Chưa có quota nào. Hãy tạo quota mới.
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                        <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                            Tên Quota
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            Giới hạn
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            Đã điền
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            Trạng thái
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            Điều kiện
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            Hành động
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {quotas.map((quota) => (
                        <TableRow
                            key={quota.id}
                            sx={{
                                '&:hover': {
                                    backgroundColor: '#f9f9f9',
                                },
                            }}
                        >
                            <TableCell>{quota.attributes.name}</TableCell>
                            <TableCell align="center">
                                {quota.attributes.limit}
                            </TableCell>
                            <TableCell align="center">
                                {quota.attributes.filled_quantity}
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={
                                        quota.attributes.status === 'active'
                                            ? 'Hoạt động'
                                            : 'Không hoạt động'
                                    }
                                    color={
                                        quota.attributes.status === 'active'
                                            ? 'success'
                                            : 'default'
                                    }
                                    size="small"
                                    variant="outlined"
                                />
                            </TableCell>
                            <TableCell align="left">
                                {renderConditions(quota)}
                            </TableCell>
                            <TableCell align="center">
                                <Stack
                                    direction="row"
                                    justifyContent="center"
                                    spacing={0.5}
                                >
                                    <Chip
                                        label={
                                            quota.attributes.status === 'active'
                                                ? 'Bật'
                                                : 'Tắt'
                                        }
                                        onClick={() =>
                                            handleToggleStatus(quota)
                                        }
                                        color={
                                            quota.attributes.status === 'active'
                                                ? 'success'
                                                : 'default'
                                        }
                                        size="small"
                                        variant="filled"
                                        disabled={
                                            updatingStatusId ===
                                            String(quota.id)
                                        }
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </Stack>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default QuotaTable;
